import { Button } from "@/components/ui/baseUi/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import dayjs, { type Dayjs } from "dayjs"
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"

type KPIPeriodPickerProps = {
  periodLabel: string
  focusYear: number
  focusMonth: number
  comparisonYear: number
  comparisonMonth: number
  onFocusMonthChange: (year: number, month: number) => void
  onComparisonMonthChange: (year: number, month: number) => void
  isMonthDisabledForFocus: (month: Dayjs) => boolean
  isMonthDisabledForComparison: (month: Dayjs) => boolean
}

export function KPIPeriodPicker({
  periodLabel,
  focusYear,
  focusMonth,
  comparisonYear,
  comparisonMonth,
  onFocusMonthChange,
  onComparisonMonthChange,
  isMonthDisabledForFocus,
  isMonthDisabledForComparison,
}: KPIPeriodPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [tempFocusYear, setTempFocusYear] = useState(focusYear)
  const [tempFocusMonth, setTempFocusMonth] = useState(focusMonth)
  const [tempComparisonYear, setTempComparisonYear] = useState(comparisonYear)
  const [tempComparisonMonth, setTempComparisonMonth] = useState(comparisonMonth)

  const MONTHS_SHORT = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format("MMM"))

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setTempFocusYear(focusYear)
      setTempFocusMonth(focusMonth)
      setTempComparisonYear(comparisonYear)
      setTempComparisonMonth(comparisonMonth)
    }
    setIsOpen(open)
  }

  const handleConfirm = () => {
    onComparisonMonthChange(tempComparisonYear, tempComparisonMonth)
    onFocusMonthChange(tempFocusYear, tempFocusMonth)
    setIsOpen(false)
  }

  const handleCancel = () => {
    setIsOpen(false)
  }

  const isTempMonthDisabledForComparison = (month: Dayjs) => {
    const now = dayjs()
    const isCurrentMonth = month.isSame(now, "month")
    const tempFocus = dayjs().year(tempFocusYear).month(tempFocusMonth)
    const isSameAsFocus = month.isSame(tempFocus, "month")
    return isCurrentMonth || isSameAsFocus || isMonthDisabledForComparison(month)
  }

  const isTempMonthDisabledForFocus = (month: Dayjs) => {
    return isMonthDisabledForFocus(month)
  }

  return (
    <div className="flex items-center gap-0 rounded-full border border-interactive-border-neutral-default bg-surface-foreground">
      <div className="flex items-center gap-1 px-4 py-2">
        <Text fontStyle="BM" className="text-textOnSurface-disabled">
          {word("boxDashboard.kpi.monthToMonth")}
        </Text>
        <ChevronDown className="size-4 text-textOnSurface-disabled" />
      </div>

      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={
            <button className="flex items-center gap-1 rounded-r-full bg-surface-background px-4 py-3">
              <Text fontStyle="BM" className="text-textOnSurface-muted">
                {periodLabel.split("-")[0] + " -"}
              </Text>
              <Text fontStyle="BM" className="text-textDefault-default font-bold text-textOnSurface-subtle">
                {periodLabel.split("-")[1]}
              </Text>
              <Calendar className="ml-1 size-4 text-textOnSurface-subtle" color="currentColor" />
            </button>
          }
        />
        <PopoverContent
          className="flex w-auto flex-col gap-6 rounded-2xl border-none bg-surface-background-float p-4"
          align="end"
        >
          <div className="flex gap-6">
            <MonthPicker
              title={word("boxDashboard.kpi.comparison")}
              year={tempComparisonYear}
              month={tempComparisonMonth}
              onYearChange={setTempComparisonYear}
              onMonthChange={(m) => setTempComparisonMonth(m)}
              isMonthDisabled={isTempMonthDisabledForComparison}
              monthsShort={MONTHS_SHORT}
            />
            <MonthPicker
              title={word("boxDashboard.kpi.baseline")}
              year={tempFocusYear}
              month={tempFocusMonth}
              onYearChange={setTempFocusYear}
              onMonthChange={(m) => setTempFocusMonth(m)}
              isMonthDisabled={isTempMonthDisabledForFocus}
              monthsShort={MONTHS_SHORT}
              isHighlighted
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={handleCancel}>
              <Text fontStyle="LM">{word("global.cancel")}</Text>
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              <Text fontStyle="LM">{word("global.ok")}</Text>
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

type MonthPickerProps = {
  title: string
  year: number
  month: number
  onYearChange: (year: number) => void
  onMonthChange: (month: number) => void
  isMonthDisabled: (month: Dayjs) => boolean
  monthsShort: string[]
  isHighlighted?: boolean
}

function MonthPicker({
  title,
  year,
  month,
  onYearChange,
  onMonthChange,
  isMonthDisabled,
  monthsShort,
  isHighlighted = false,
}: MonthPickerProps) {
  return (
    <div className={cn("flex flex-col gap-4 p-4", isHighlighted && "rounded-xl bg-surface-foreground")}>
      <Text fontStyle="LM" className={cn("text-center font-semibold", isHighlighted ? "" : "text-textOnSurface-muted")}>
        {title}
      </Text>

      <div className="flex items-center justify-between">
        <button
          className="rounded-full p-1 hover:bg-interactive-background-primary-highlighted"
          onClick={() => onYearChange(year - 1)}
        >
          <ChevronLeft className="size-5" />
        </button>
        <Text fontStyle="BM">{year}</Text>
        <button
          className="rounded-full p-1 hover:bg-interactive-background-primary-highlighted"
          onClick={() => onYearChange(year + 1)}
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {monthsShort.map((name, index) => {
          const monthDate = dayjs().year(year).month(index)
          const isSelected = month === index
          const isDisabled = isMonthDisabled(monthDate)

          return (
            <button
              key={name}
              disabled={isDisabled}
              className={cn(
                "rounded-full px-3 py-2 text-sm",
                isDisabled && "cursor-not-allowed text-textOnSurface-disabled",
                !isDisabled && "hover:bg-interactive-background-primary-highlighted",
                isSelected &&
                  !isDisabled &&
                  "bg-surface-background-primary-default hover:bg-interactive-background-primary-highlighted",
              )}
              onClick={() => !isDisabled && onMonthChange(index)}
            >
              <Text fontStyle="BM">{name}</Text>
            </button>
          )
        })}
      </div>
    </div>
  )
}
